import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { addNewUser } from "../miscellaneous/addNewUser";
import { Asterisk } from "../../common/components/Asterisk";
import { SectionTitle } from "../../common/components/SectionTitle";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { fetchGetCompaniesByParent } from "../../organization-management/miscellaneous/fetchGetCompaniesByParent";
import { fetchGetJobsByCompanyId } from "../../jobs-management/miscellaneous/fetchGetJobsByCompanyId";
import { ImportUsersModal } from "./ImportUsersModal";
import { AxiosError } from "axios";
import { CapchaTokenModal } from "../../task-submission/components/CapchaTokenModal";
import { fetchGetAllParentCompanies } from "../miscellaneous/fetchGetAllParentCompanies";

const PARTICIPANT_TYPES = [
  { value: 1, label: "Time Study Participant" },
  { value: 2, label: "Ops Work Log Participant" },
];

const formControlStyle = {
  marginTop: "20px",
};

const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "60px",
};

export const CreateNewUser: FC = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    setError,
    reset,
    clearErrors,
    watch,
  } = useForm();
  const navigate = useNavigate();

  const [companiesName, setCompaniesName] = useState([]);
  const [jobTitle, setJobTitle] = useState([]);

  const [isShowCapchaModal, setIsShowCapchaModal] = useState(false);
  const [captChaToken, setCaptChaToken] = useState(null);

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleSupperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const callerParentCompanyId = localStorage.getItem("parentCompanyId") ?? "";
  const callerParentCompanyName = localStorage.getItem("parentCompanyName") ?? "";

  const [parentCompanies, setParentCompanies] = useState<{ id: string; name: string }[]>(
    !isRoleSupperAdmin && !!callerParentCompanyId
      ? [{ id: callerParentCompanyId, name: callerParentCompanyName }]
      : []
  );

  const selectedRole = watch("role");
  const showCompany = selectedRole !== "Super Admin";
  const showOrg = selectedRole === "User";
  const showJobTitle = selectedRole === "User";
  const showParticipantType = selectedRole === "User";

  const mutationGetParentCompanies = useMutation(fetchGetAllParentCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companies = _.map(res.data.data, (item: any) => ({
          id: item.id,
          name: item.companyName,
        }));
        setParentCompanies(companies as any);
      }
    },
    onError: () => {},
  });

  const mutationGetCompaniesByParent = useMutation(fetchGetCompaniesByParent, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companyName = _.map(res.data.data, (item: any) => ({
          name: item.companyName,
          value: item.id,
        }));
        setCompaniesName(companyName as any);
      }
    },
    onError: () => {},
  });

  const mutationGetJobsByCompany = useMutation(fetchGetJobsByCompanyId, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const jobs = _.map(res.data.data ?? [], (item) => {
          return { id: item.id, name: item.title };
        });

        setJobTitle(jobs as any);
      }
    },
    onError: () => { },
  });

  const mutationAddUser = useMutation(addNewUser, {
    onSuccess: (response) => {
      if (response && response.status === 200) {
        navigate("/user-management");
      }
    },
    onError: (error: AxiosError) => {
      if (
        error.response &&
        error.response.status === 400
        &&
        (error.response.data as string).includes("already taken")
      ) {
        reset(
          {},
          {
            keepValues: true,
          }
        );

        setError("email", {
          type: "manual",
          message: "The e-mail address is already in use",
        });
      }
    },
  });

  const [isShowImportModal, setIsShowImportModal] = useState(false);

  useEffect(() => {
    if (isRoleSupperAdmin) {
      mutationGetParentCompanies.mutate();
    } else if (callerParentCompanyId) {
      mutationGetCompaniesByParent.mutate(callerParentCompanyId);
    }
  }, []);

  const onSubmit = (data: any) => {

    if (!captChaToken) {
      setIsShowCapchaModal(true);
    } else {
      const isActive = getValues("status") === "active" ? true : false;
      const role = data.role;

      const companyName = role === "User"
        ? _.find(companiesName ?? [], (item: any) => item.value === data.organization)
        : null;

      // Find parent company name for Admin role
      const parentCompany = role === "Admin"
        ? _.find(parentCompanies ?? [], (item: any) => item.id === data.parentCompany)
        : null;

      const userData = {
        ...data,
        jobTitleId: role === "User" ? data.jobTitle : null,
        companyName: companyName?.name ?? null,
        isActive: isActive,
        participantType: data.participantType || 1,
        parentCompanyId: role === "Admin" ? data.parentCompany : null,
      };
  
      mutationAddUser.mutate(userData);
    }
  };

  const handleCancel = () => {
    navigate("/user-management");
  };

  return isRoleSupperAdmin || parentCompanies.length > 0 ? (
    <>
      <div className="mt-9">
        <div className="flex flex-row items-center justify-between">
          <SectionTitle name="Create New User" />
          <ButtonCancelA
            name={"Import from file"}
            onHandleClick={() => {
              setIsShowImportModal(true);
            }}
          />
        </div>
        <Box sx={{ minWidth: 120, marginTop: "60px" }}>
          <form className="pr-24 pl-24 pb-24" onSubmit={handleSubmit(onSubmit)}>
            {showCompany && (
            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">
                Company <Asterisk />
              </label>
              <Controller
                control={control}
                name="parentCompany"
                defaultValue={isRoleSupperAdmin ? "" : callerParentCompanyId}
                rules={{ required: "Company is required" }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="parentCompany"
                    value={value ?? ""}
                    disabled={!isRoleSupperAdmin}
                    onChange={(e) => {
                      setValue("parentCompany", e.target.value);
                      clearErrors("parentCompany");
                      setValue("organization", "");
                      setValue("jobTitle", "");
                      setJobTitle([]);
                      setCompaniesName([]);
                      if (e.target.value) {
                        mutationGetCompaniesByParent.mutate(e.target.value);
                      }
                    }}
                    style={{ height: "38px" }}
                  >
                    {parentCompanies.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              ></Controller>
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("parentCompany").error?.message}
              </FormHelperText>
            </FormControl>
            )}

            {showOrg && (
            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">
                Organization <Asterisk />
              </label>
              <Controller
                control={control}
                name="organization"
                rules={{ required: "Organization is required" }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="organization"
                    value={value ?? ""}
                    onChange={(e) => {
                      setValue("organization", e.target.value);
                      clearErrors("organization");
                      mutationGetJobsByCompany.mutate(e.target.value);
                    }}
                    style={{ height: "38px" }}
                  >
                    {(companiesName ?? []).map((item: any) => {
                      return (
                        <MenuItem key={item.value} value={item.value}>
                          {item.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              ></Controller>
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("organization").error?.message}
              </FormHelperText>
            </FormControl>
            )}

            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">
                User Email <Asterisk />
              </label>
              <TextField
                {...register("email", {
                  required: "User Email is required",
                  pattern:
                    /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                })}
                defaultValue={""}
                onChange={() => {
                  clearErrors("email");
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    height: "5px",
                  },
                }}
              />
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("email").error?.message}
                {errors.email &&
                  !control.getFieldState("email").error?.message && (
                    <span>Please enter valid email address</span>
                  )}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">
                Full Name <Asterisk />
              </label>
              <TextField
                {...register("fullName", { required: "Full Name is required" })}
                defaultValue={""}
                sx={{
                  "& .MuiInputBase-input": {
                    height: "5px",
                  },
                }}
              />
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("fullName").error?.message}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">
                Role <Asterisk />
              </label>
              <Controller
                control={control}
                name="role"
                defaultValue={"User"}
                rules={{ required: "Role is required" }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="user-role"
                    value={value ?? ""}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setValue("role", newRole);
                      clearErrors("role");

                      // Clear fields that will be hidden based on new role
                      if (newRole === "Super Admin") {
                        setValue("parentCompany", "");
                        setValue("organization", "");
                        setValue("jobTitle", "");
                        setValue("participantType", "");
                        clearErrors("parentCompany");
                        clearErrors("organization");
                        clearErrors("jobTitle");
                        clearErrors("participantType");
                        setCompaniesName([]);
                        setJobTitle([]);
                      } else if (newRole === "Admin") {
                        setValue("organization", "");
                        setValue("jobTitle", "");
                        setValue("participantType", "");
                        clearErrors("organization");
                        clearErrors("jobTitle");
                        clearErrors("participantType");
                        setJobTitle([]);
                      }
                    }}
                    style={{ height: "38px" }}
                  >
                    {isRoleSupperAdmin && (
                      <MenuItem value={"Super Admin"}>Super Admin</MenuItem>
                    )}
                    <MenuItem value={"Admin"}>Admin</MenuItem>
                    <MenuItem value={"User"}>User</MenuItem>
                  </Select>
                )}
              ></Controller>
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("role").error?.message}
              </FormHelperText>
            </FormControl>

            {showParticipantType && (
            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">
                Participant Type <Asterisk />
              </label>
              <Controller
                control={control}
                name="participantType"
                defaultValue={1}
                rules={{ required: "Participant Type is required" }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="participantType"
                    value={value ?? 1}
                    onChange={(e) => {
                      setValue("participantType", e.target.value);
                      clearErrors("participantType");
                    }}
                    style={{ height: "38px" }}
                  >
                    {PARTICIPANT_TYPES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              ></Controller>
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("participantType").error?.message}
              </FormHelperText>
            </FormControl>
            )}

            {showJobTitle && (
            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">Job Title <Asterisk /></label>
              <Controller
                control={control}
                name="jobTitle"
                rules={{ required: "Job Title is required" }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="user-job-title"
                    value={value ?? ""}
                    onChange={(e) => {
                      setValue("jobTitle", e.target.value);
                      clearErrors("jobTitle");
                    }}
                    style={{ height: "38px" }}
                  >
                    {(jobTitle ?? []).map((item: any) => {
                      return (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              ></Controller>
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("jobTitle").error?.message}
              </FormHelperText>
            </FormControl>
            )}

            <FormControl fullWidth sx={formControlStyle}>
              <label className="font-bold">Status</label>
              <Controller
                control={control}
                name="status"
                defaultValue={"active"}
                rules={{ required: "Status is required" }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="status"
                    value={value ?? "active"}
                    onChange={(e) => {
                      setValue("status", e.target.value);
                      clearErrors("status");
                    }}
                    style={{ height: "38px" }}
                  >
                    <MenuItem value={"active"}>Active</MenuItem>
                    <MenuItem value={"inactive"}>Inactive</MenuItem>
                  </Select>
                )}
              ></Controller>
              <FormHelperText sx={{ color: "red" }}>
                {control.getFieldState("status").error?.message}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth sx={btnGroupStyle}>
              <ButtonCancelA
                name="Cancel"
                onHandleClick={handleCancel}
                icon={<CancelIcon sx={{ marginRight: "4px" }} />}
              />
              <ButtonSubmitA
                name="Save"
                icon={<SaveIcon sx={{ marginRight: "4px" }} />}
              />
            </FormControl>
          </form>
        </Box>
      </div>

      <ImportUsersModal
        companiesName={companiesName}
        isShowImportModal={isShowImportModal}
        setIsShowImportModal={setIsShowImportModal}
      />

      <CapchaTokenModal
        isShowModal={isShowCapchaModal}
        setIsShowModal={setIsShowCapchaModal}
        setCaptChaToken={setCaptChaToken as any}
        content={"Please verify you are human before add new user processing"}
      />
    </>
  ) : null;
};
